import { DocumentStatusV2Schema, DocumentWithDownloadUrl as Document } from '@/client';
import { CRITICAL_INFORMATION_TAG, RecordTag, USER_DOCUMENT_LINK_TAG, USER_NOTE_TAG } from '@/constants/documentTags';
import { CriticalInfoDocument } from '@/types/CriticalInfo';
import { hasImageExtension } from '@/utils/upload/uploadUtils.ts';

/**
 * Utility functions for working with document types and display names
 */

// Constants for text truncation
export const MAX_TEXT_LENGTH = 50;
export const TRUNCATED_TEXT_LENGTH = 47; // MAX_TEXT_LENGTH - 3 (for "...")

/** Classification categories intentionally hidden from the records UI. */
export const HIDDEN_CLASSIFICATION_TAG_KEYS = new Set(['media']);

export type NoteTextSegment = { type: 'text'; text: string } | { type: 'url'; text: string; href: string };

const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const TRAILING_URL_PUNCTUATION = /[),.;:!?]+$/;

const getUrlHref = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

export const getNoteTextSegments = (text: string): NoteTextSegment[] => {
	const segments: NoteTextSegment[] = [];
	let cursor = 0;

	for (const match of text.matchAll(URL_PATTERN)) {
		const rawMatch = match[0];
		const index = match.index ?? 0;
		const url = rawMatch.replace(TRAILING_URL_PUNCTUATION, '');
		const trailing = rawMatch.slice(url.length);

		if (index > cursor) {
			segments.push({ type: 'text', text: text.slice(cursor, index) });
		}

		segments.push({ type: 'url', text: url, href: getUrlHref(url) });

		if (trailing) {
			segments.push({ type: 'text', text: trailing });
		}

		cursor = index + rawMatch.length;
	}

	if (cursor < text.length) {
		segments.push({ type: 'text', text: text.slice(cursor) });
	}

	return segments.length ? segments : [{ type: 'text', text }];
};

/**
 * Returns the type tag for a document based on its type
 *
 * The API does not provide a type field, so we infer the type from tags.
 */
export const getTypeTag = (document: Document): RecordTag | null => {
	if (isNote(document)) {
		return USER_NOTE_TAG;
	} else if (isLink(document)) {
		return USER_DOCUMENT_LINK_TAG;
	} else if (isImportant(document)) {
		return CRITICAL_INFORMATION_TAG;
	} else {
		return null;
	}
};
/**
 * Checks if a document has a specific tag
 */
const hasTag = (document: Document, tagName: string): boolean => {
	return document.tags?.some((tag) => tag.name === tagName || tag.key === tagName) || false;
};

/**
 * Checks if a document has a specific classification tag by key
 * Used for filtering documents by classification categories
 */
export const hasClassificationTagByKey = (document: Document, tagKey: string): boolean => {
	if (HIDDEN_CLASSIFICATION_TAG_KEYS.has(tagKey)) return false;
	return document.tags?.some((tag) => tag.key === tagKey && tag.type === 'classification') || false;
};

/**
 * Returns the document's classification tags (the auto-generated category tags),
 * or an empty array when the document or its tags are absent.
 */
export const getClassificationTags = (document?: Document | null): NonNullable<Document['tags']> => {
	return (
		document?.tags?.filter(
			(tag) => tag.type === 'classification' && !HIDDEN_CLASSIFICATION_TAG_KEYS.has(tag.key),
		) ?? []
	);
};

/**
 * Returns the document's generated classification. Ingestion currently assigns one classification;
 * the API uses the generic tags collection because documents also carry non-classification tags.
 */
export const getClassificationTag = (document?: Document | null): NonNullable<Document['tags']>[number] | undefined => {
	// Reuse the filtered list so every caller consistently excludes hidden categories; document tag lists are small.
	return getClassificationTags(document)[0];
};

/**
 * Returns the corrections the owner saved against this document, or an empty array when it has none.
 *
 * These arrive on the documents list itself, so no record row or drawer needs its own request. The
 * house-wide corrections list exists for the ones a document can't carry — a property fact amends no
 * document — not as the source for these.
 */
export const getDocumentCorrections = (document?: Document | null): NonNullable<Document['corrections']> => {
	return document?.corrections ?? [];
};

/**
 * Checks if a document is a note
 */
export const isNote = (document: Document): boolean => {
	return hasTag(document, USER_NOTE_TAG);
};

/**
 * Checks if a document is a link
 */
export const isLink = (document: Document): boolean => {
	return hasTag(document, USER_DOCUMENT_LINK_TAG);
};

/**
 * Checks if a document is marked important
 */
export const isImportant = (document: Document): boolean => {
	return hasTag(document, CRITICAL_INFORMATION_TAG);
};

/**
 * Checks if a document is a regular file (not a note, link, important, or contact)
 */
export const isFile = (document: Document): boolean => {
	return !(isNote(document) || isLink(document) || isImportant(document));
};

/**
 * Formats a note document title using the first line of its description
 */
export const displayNoteRecord = (document: Document): string => {
	if (!document.description) return document.filename;

	const firstLine = document.description.split('\n')[0] || '';
	return firstLine.length > MAX_TEXT_LENGTH ? `${firstLine.substring(0, TRUNCATED_TEXT_LENGTH)}...` : firstLine;
};

/**
 * Formats a link document title using its JSON data
 */
export const displayLinkRecord = (document: Document): string => {
	if (!document.description) return document.filename;

	try {
		const linkData = JSON.parse(document.description);

		// If we have a title, use it; otherwise use the URL
		if (linkData.title) {
			return linkData.title;
		} else if (linkData.url) {
			// Truncate long URLs
			return linkData.url.length > MAX_TEXT_LENGTH
				? `${linkData.url.substring(0, TRUNCATED_TEXT_LENGTH)}...`
				: linkData.url;
		}
	} catch (e) {
		// If JSON parsing fails, fall back to filename
		console.error('Failed to parse link JSON:', e);
	}

	return document.filename;
};

/**
 * Formats an important document title with system names
 */
export const displayImportantRecord = (document: Document): string => {
	if (!document.description) return 'Important systems';

	try {
		const criticalInfo = JSON.parse(document.description) as CriticalInfoDocument;
		if (criticalInfo.systems && criticalInfo.systems.length > 0) {
			const systemNames = criticalInfo.systems.map((system) => system.name);
			return `Important systems: ${systemNames.join(', ')}`;
		}
	} catch (e) {
		// If JSON parsing fails, fall back to generic title
		console.error('Failed to parse critical info JSON:', e);
	}

	return 'Important systems';
};

/**
 * Formats a file document title using its filename
 */
export const displayFileRecord = (document: Document): string => {
	return document.filename;
};

/**
 * Formats a contact document title using its filename
 */
export const displayContactRecord = (document: Document): string => {
	return document.filename;
};

/**
 * Formats a document title based on its type
 *
 * Delegates to specific display functions based on document type
 */
export const displayRecordName = (document: Document): string => {
	if (isNote(document)) {
		return displayNoteRecord(document);
	}

	if (isLink(document)) {
		return displayLinkRecord(document);
	}

	if (isImportant(document)) {
		return displayImportantRecord(document);
	}

	// Default to file display
	return displayFileRecord(document);
};

/**
 * Checks if a document is a PDF file based on its filename
 */
export const isPdf = (document: Document): boolean => {
	return document.filename.toLowerCase().endsWith('.pdf');
};

/**
 * Checks if a document is an image file based on its filename.
 * Centralized to reuse uploadUtils helpers for consistency.
 */
export const isImage = (document: Document): boolean => {
	return hasImageExtension(document.filename);
};

/**
 * Checks if a document is supported by react-doc-viewer
 */
export const isSupportedByDocViewer = (document: Document): boolean => {
	const filename = document.filename.toLowerCase();
	const supportedExtensions = [
		'.doc',
		'.docx',
		'.xls',
		'.xlsx',
		'.ppt',
		'.pptx',
		'.txt',
		'.csv',
		'.rtf',
		'.odt',
		'.ods',
		'.odp',
	];
	return supportedExtensions.some((ext) => filename.endsWith(ext));
};

/**
 * Gets the file extension from a document filename
 */
export const getFileExtension = (document: Document): string => {
	const parts = document.filename.toLowerCase().split('.');
	return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
};

/**
 * Checks if a document upload is complete by verifying the uploaded_at field is set
 */
export const isUploadComplete = (doc?: DocumentStatusV2Schema | Document | null): boolean => {
	return !!doc?.uploaded_at;
};

export const documentSelectors = {
	findById: (documents: Document[], id: number) => documents.find((q) => q.id === id),
	findByFilename: (documents: Document[], filename: string) => documents.find((d) => d.filename === filename),
};

/**
 * Finds a document by its numeric DB id.
 * Convenience wrapper around documentSelectors.findById.
 */
export const findDocumentById = (documents: Document[], id: number): Document | undefined =>
	documentSelectors.findById(documents, id);
/**
 * Checks if a document deletion is complete by verifying the is_deleted flag is true
 */
export const isDeleteComplete = (doc?: DocumentStatusV2Schema | null): boolean => {
	return doc?.is_deleted === true;
};
